import { useState, useEffect, useRef } from "react";
import InputText from "./InputText";
import Time from "./Time";
import EntryTypeSelector from "./dayplan/EntryTypeSelector";
import ActivityEntry from "./dayplan/ActivityEntry";
import TransportEntry from "./dayplan/TransportEntry";
import HotelEntry from "./dayplan/HotelEntry";
import { normalizeTimeSlot } from "../utils/normalizeTimeSlot";
import { validateFile } from "../utils/helper";

const EMPTY_PLAN = {
  entryType: "activity",
  time: "00:00:00",
  title: "",
  description: "",
  image: "",
  lat: "",
  lng: "",
  transportType: "onward",
  transportOption: null,
  hotelType: null,
  hotelName: null,
  hotelOptions: null,
  noofRooms: "",
};

const initSlot = (slot, index) => {
  const normalized = normalizeTimeSlot(slot);
  return {
    id: normalized._id || index + 1,
    isSaved: !!normalized._id,
    entryType: normalized.entryType || "activity",
    time: normalized.time || "00:00:00",
    title: normalized.title || normalized.planDetails || normalized.planDetail || "",
    description: normalized.description || "",
    image: normalized.image || "",
    lat: normalized.lat || "",
    lng: normalized.lng || "",
    transportType: normalized.transportType || "onward",
    transportOption: normalized.transportOption || null,
    hotelType: normalized.hotelType || null,
    hotelName: normalized.hotelName || null,
    hotelOptions: normalized.hotelOptions || null,
    noofRooms: normalized.noofRooms || "",
  };
};

const DayPlan = ({
  dayNumber,
  initialTimeSlots = [],
  planName01 = "",
  daywiseImage = null,
  onChange,
  onDelete,
  setDays,
  onwardTransportOptions = [],
  returnTransportOptions = [],
  transferOptions = [],
  hotelNameOptions = [],
  hotelTypeOptions = [],
  isFirstDay,
  isLastDay,
  onMoveUp,
  onMoveDown,
}) => {
  console.log("🚀 ~ DayPlan ~ returnTransportOptions:", returnTransportOptions);
  console.log("🚀 ~ DayPlan ~ onwardTransportOptions:", onwardTransportOptions);
  console.log("🚀 ~ DayPlan ~ transferOptions:", transferOptions);
  const [plans, setPlans] = useState(
    initialTimeSlots.length > 0
      ? initialTimeSlots.map(initSlot)
      : [{ ...EMPTY_PLAN, id: 1, isSaved: false }]
  );
  const [planName, setPlanName] = useState(planName01);
  const [isOpen, setIsOpen] = useState(true);
  const [expandedSlots, setExpandedSlots] = useState({});


  useEffect(() => {
    const timeSlots = plans.map((plan) => {
      const base = { entryType: plan.entryType, time: plan.time, title: plan.title };
      if (plan.entryType === "activity") {
        return { ...base, planDetails: plan.title, description: plan.description, image: plan.image, lat: plan.lat ? parseFloat(plan.lat) : null, lng: plan.lng ? parseFloat(plan.lng) : null };
      }
      if (plan.entryType === "transport") {
        return { ...base, transportType: plan.transportType, transportOption: plan.transportOption };
      }
      if (plan.entryType === "hotel") {
        return { ...base, hotelType: plan.hotelType, hotelName: plan.hotelName, hotelOptions: plan.hotelOptions, noofRooms: plan.noofRooms };
      }
      if (plan.entryType === "meal") {
        return base;
      }
      return base;
    });
    onChange({ dayNumber, planName, timeSlots, daywiseImage });
  }, [plans, planName, dayNumber, daywiseImage]);

  const addPlanDetail = () => {
    setPlans((prev) => [...prev, { ...EMPTY_PLAN, id: Date.now(), isSaved: false }]);
  };

  const removePlanDetail = (id) => {
    setPlans((prev) => prev.filter((plan) => plan.id !== id));
  };

  const updatePlanDetail = (id, key, value) => {
    setPlans((prev) => prev.map((plan) => (plan.id === id ? { ...plan, [key]: value } : plan)));
  };

  const handleEntryTypeChange = (id, newType) => {
    setPlans((prev) =>
      prev.map((plan) => {
        if (plan.id !== id) return plan;
        if (plan.entryType === newType) return plan;
        return { ...plan, entryType: newType };
      })
    );
    setExpandedSlots((prev) => ({ ...prev, [id]: true }));
  };



  const toggleExpand = (id) => {
    setExpandedSlots((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const movePlanUp = (index) => {
    if (index === 0) return;
    setPlans((prev) => {
      const newPlans = [...prev];
      [newPlans[index - 1], newPlans[index]] = [newPlans[index], newPlans[index - 1]];
      return newPlans;
    });
  };

  const movePlanDown = (index) => {
    if (index === plans.length - 1) return;
    setPlans((prev) => {
      const newPlans = [...prev];
      [newPlans[index], newPlans[index + 1]] = [newPlans[index + 1], newPlans[index]];
      return newPlans;
    });
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border transition-all ${isOpen ? "border-l-4" : "border-gray-200"}`}>
      {/* Accordion Header */}
      <div
        className="bg-gray-50 px-5 py-4 rounded-t-xl cursor-pointer select-none hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen((v) => !v)}
      >
        <div className="flex items-center gap-3">
          {/* Day badge */}
          <span className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm">
            {dayNumber}
          </span>

          {/* Plan name — stop click propagation so typing doesn't toggle */}
          <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
            <InputText
              placeholder="Enter plan name (e.g. Bangalore - Hyderabad)..."
              value={planName}
              onChange={(e) => setPlanName(e.target.value.replace(/'/g, "`"))}
            />
          </div>



          <div className="flex gap-1 items-center">
            {onMoveUp && !isFirstDay && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center h-8 w-8 shrink-0 min-w-[32px] min-h-[32px]"
                type="button"
                title="Move Day Up"
              >
                <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>arrow_upward</span>
              </button>
            )}
            {onMoveDown && !isLastDay && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center h-8 w-8 shrink-0 min-w-[32px] min-h-[32px]"
                type="button"
                title="Move Day Down"
              >
                <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>arrow_downward</span>
              </button>
            )}
          </div>

          {/* Delete day */}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors flex items-center justify-center h-8 w-8 shrink-0 min-w-[32px] min-h-[32px]"
            type="button"
          >
            <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>delete</span>
          </button>

          {/* Chevron */}
          <span className={`material-symbols-outlined text-xl text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}>
            expand_more
          </span>
        </div>
      </div>

      {/* Time Slots — collapsible */}
      {isOpen && <div className="p-5 border-t border-gray-200">
        <div className="space-y-4">
          {plans.map((plan, index) => (
            <div
              key={plan.id}
              className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-visible"
            >
              {/* Card Header border-b */}
              <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50/50 gap-3">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <EntryTypeSelector
                    value={plan.entryType}
                    disabled={plan.isSaved}
                    onChange={(type) => handleEntryTypeChange(plan.id, type)}
                  />
                </div>

                <div className="flex items-center gap-2 justify-end">
                  {index > 0 && (
                    <button
                      onClick={() => movePlanUp(index)}
                      className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center h-8 w-8"
                      type="button"
                      title="Move Plan Up"
                    >
                      <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>arrow_upward</span>
                    </button>
                  )}
                  {index < plans.length - 1 && (
                    <button
                      onClick={() => movePlanDown(index)}
                      className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center h-8 w-8"
                      type="button"
                      title="Move Plan Down"
                    >
                      <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>arrow_downward</span>
                    </button>
                  )}
                  <button
                    onClick={() => removePlanDetail(plan.id)}
                    className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors flex items-center justify-center h-8 w-8"
                    type="button"
                    title="Delete Entry"
                  >
                    <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>delete</span>
                  </button>
                  <button
                    onClick={() => toggleExpand(plan.id)}
                    className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors flex items-center justify-center h-8 w-8"
                    type="button"
                    title={expandedSlots[plan.id] ? "Collapse Entry" : "Expand Entry"}
                  >
                    <span className="material-symbols-outlined shrink-0" style={{ fontSize: "18px" }}>
                      {expandedSlots[plan.id] ? "expand_less" : "expand_more"}
                    </span>
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-4 lg:col-span-4">
                    <label className="text-sm font-medium text-gray-600 mb-1 block">Time</label>
                    <Time
                      value={plan.time}
                      onChange={(e) => updatePlanDetail(plan.id, "time", e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-8 lg:col-span-8">
                    <label className="text-sm font-medium text-gray-600 mb-1 block">
                      {plan.entryType.charAt(0).toUpperCase() + plan.entryType.slice(1)} Title
                    </label>
                    <InputText
                      placeholder={`e.g. Enter ${plan.entryType} title...`}
                      value={plan.title}
                      onChange={(e) => {
                        const safeValue = e.target.value.replace(/'/g, "`");
                        updatePlanDetail(plan.id, "title", safeValue);
                      }}
                    />
                  </div>
                </div>

                {expandedSlots[plan.id] && plan.entryType === "activity" && (
                  <div className="pt-4 border-t border-gray-100">
                    <ActivityEntry plan={plan} onUpdate={(key, value) => updatePlanDetail(plan.id, key, value)} />
                  </div>
                )}
                
                {expandedSlots[plan.id] && plan.entryType === "transport" && (
                  <div className="pt-4 border-t border-gray-100">
                    <TransportEntry 
                      plan={plan} 
                      onUpdate={(key, value) => updatePlanDetail(plan.id, key, value)} 
                      returnOptions={returnTransportOptions}
                      onwardOptions={onwardTransportOptions}
                      transferOptions={transferOptions}
                    />
                  </div>
                )}

                {expandedSlots[plan.id] && plan.entryType === "hotel" && (
                  <div className="pt-4 border-t border-gray-100">
                    <HotelEntry plan={plan} onUpdate={(key, value) => updatePlanDetail(plan.id, key, value)} hotelTypeOptions={hotelTypeOptions} hotelNameOptions={hotelNameOptions} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Entry button */}
        <button
          onClick={addPlanDetail}
          type="button"
          className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <span className="material-symbols-outlined text-lg">add</span>
          Add Entry
        </button>
      </div>}
    </div>
  );
};

export default DayPlan;
